import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Stack } from '@mui/material'

import Modal from 'components/Modal'
import HeaderExportButton from 'components/global/Buttons/HeaderExportButton'
import Checkbox from 'packages/ui/Checkbox'
import { useUser } from 'store'

import { useSort } from './store'
import { useExportCustomers } from './user-customers'


type ExportProps = {
  verbatim?: string,
  countries: string[],
  startDate?: Date | null
  endDate?: Date | null
}

const FIELDS = [
  { name: 'email', defaultChecked: true },
  { name: 'civility', defaultChecked: true },
  { name: 'first_name', defaultChecked: true },
  { name: 'last_name', defaultChecked: true },
  { name: 'created_at', defaultChecked: true },
  { name: 'country', defaultChecked: false },
  { name: 'browser_name', defaultChecked: false },
  { name: 'os_name', defaultChecked: false },
  { name: 'visit_reason', defaultChecked: false },
]

const ExportModal = ({ onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false)
  const [showSummary, setShowSummary] = useState(false)
  const [fields, setFields] = useState(FIELDS.filter(({ defaultChecked }) => defaultChecked).map(({ name }) => name))
  const { t } = useTranslation()
  const [user] = useUser()

  const handleToggleField = (name: string) => () => {
    setFields(fields.includes(name)
      ? fields.filter(field => field !== name)
      : [...fields, name]
    )
  }

  const handleFieldKeyDown = (name: string) => (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }
    event.preventDefault()
    handleToggleField(name)()
  }

  return (
    <Modal
      onClose={onClose}
      icon={<i className={'fa-solid fa-arrow-up-from-line'} />}
      title={t('stats.customers.export_modal_title')}
      minWidth={'400px'}
    >
      {
        showSummary ? (
          <Box sx={{ fontSize: 13 }}>{t('stats.customers.export_modal_summary', { email: user?.email })}</Box>
        ) : (
          <Stack sx={{ fontSize: 12, color: '#7A8096' }} gap={1}>
            <Box sx={{ fontSize: 13 }}>{t('stats.customers.export_modal_fields')}</Box>
            {
              FIELDS.map(field => (
                <Stack
                  direction='row'
                  key={field.name}
                  alignItems={'center'}
                  gap={1}
                  role='checkbox'
                  aria-checked={fields.includes(field.name)}
                  tabIndex={0}
                  onClick={handleToggleField(field.name)}
                  onKeyDown={handleFieldKeyDown(field.name)}
                  sx={{ cursor: 'pointer', userSelect: 'none', width: 'fit-content' }}
                >
                  <Checkbox
                    checked={fields.includes(field.name)}
                    onChange={handleToggleField(field.name)}
                  />
                  <Box sx={{ color: '#7A8096' }}>{t(`statistics.customers.table.${field.name}`)}</Box>
                </Stack>
              ))
            }
          </Stack>
        )
      }

      <Stack direction='row' justifyContent={'end'}>
        <Button
          size='small'
          variant='text'
          onClick={onClose}
        >
          {t('ui_elements.close')}
        </Button>
        {
          !showSummary && (
            <Button
              size='small'
              color='primary'
              variant='contained'
              disabled={loading}
              onClick={async () => {
                setLoading(true)
                await onConfirm(fields)
                setLoading(false)
                setShowSummary(true)
              }}>
              {t('ui_elements.export')}
            </Button>
          )
        }
      </Stack>
    </Modal>
  )
}

const Export: React.FC<ExportProps> = ({
  verbatim,
  countries,
  startDate,
  endDate
}) => {
  const { t, i18n } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [sort] = useSort()
  const { launchExport, loading } = useExportCustomers({
    verbatim,
    countries,
    startDate,
    endDate,
    sortBy: sort.by,
    sortDirection: sort.direction,
    language: i18n.language
  })
  console.log('launchExport', launchExport)
  return (
    <>
      <HeaderExportButton
        analyticsName="stats_customers"
        onClick={async () => {
          setShowModal(true)
        }}
        loading={loading}
        loadingLabel={t('ui_elements.in_progress')}
      >
        {t('ui_elements.export')}
      </HeaderExportButton>
      {
        showModal && (
          <ExportModal
            onClose={() => setShowModal(false)}
            onConfirm={(fields) => launchExport(fields)}
          />
        )
      }
    </>
  )
}

export default Export